import type { ApplicationDraft, MiddlemanRoute } from "./types";
import { ageOnDate, requiresGuardian } from "./business";

export const APPLICATION_VERSION = "2026-07-02.3";
export const CONSENT_VERSION = "2026-07-02";
export const AVAILABILITY_BANDS = ["2-4", "5-8", "9-12", "13+"] as const;

export const STATUS_OPTIONS = [
  "Studying, can work 5-10 hours",
  "Employed, can work nights/weekends",
  "Fully available",
  "Between work, ready to start",
] as const;

export const WORK_PREFERENCES = [
  "Finding company info and checking fit",
  "Sending approved messages or follow-ups",
  "Posting helpful links in communities",
  "Making short posts or clips from supplied content",
  "Finding creator or founder profiles",
  "Let the platform pick my first task",
] as const;

export const YES_NO_OPTIONS = ["Yes", "No"] as const;

export const APPLICATION_ROUTES: Exclude<MiddlemanRoute, "unplaced">[] = [
  "clipper", "seeder", "closer", "scout", "promoter",
];

export const PLATFORM_SAMPLE = {
  version: "platform-task-check-2026-07-02.3",
  prompt: "InferenceSaver gives you a small batch of targets, a simple SOP, and proof rules. Explain how you would complete the batch, avoid spam, and show proof so a reviewer can approve the result.",
};

export const TASK_TYPE_LABELS: Record<Exclude<MiddlemanRoute, "unplaced">, string> = {
  clipper: "content tasks",
  seeder: "posting tasks",
  closer: "outreach tasks",
  scout: "research tasks",
  promoter: "creator list tasks",
};

export const ROUTE_SAMPLES: Record<Exclude<MiddlemanRoute, "unplaced">, { version: string; prompt: string }> = {
  clipper: PLATFORM_SAMPLE,
  seeder: PLATFORM_SAMPLE,
  closer: PLATFORM_SAMPLE,
  scout: PLATFORM_SAMPLE,
  promoter: PLATFORM_SAMPLE,
};

export interface RouteRecommendation {
  primaryRoute: Exclude<MiddlemanRoute, "unplaced">;
  backupRoute: Exclude<MiddlemanRoute, "unplaced">;
  scores: Record<Exclude<MiddlemanRoute, "unplaced">, number>;
  reasons: string[];
}

export function sampleFor() {
  return PLATFORM_SAMPLE;
}

export function recommendApplicationRoutes(form: ApplicationDraft): RouteRecommendation {
  const text = [
    form.workPreference,
    form.dealExperience,
    form.commissionExperience,
    form.obsession,
    form.outworkedMoment,
    form.rejectionResponse,
    form.quitReason,
    form.loyaltyUnderPressure,
    form.firstThirtyDaysPlan,
    form.experience,
    form.whyEntry,
    form.tools,
    form.sampleResponse,
  ].join(" ").toLowerCase();

  const scores: RouteRecommendation["scores"] = {
    closer: 0,
    scout: 0,
    clipper: 0,
    seeder: 0,
    promoter: 0,
  };
  const reasons: string[] = [];

  addAvailability(scores, form.availabilityBand, form.currentStatus);
  addPreference(scores, form.workPreference);
  if (form.dealExperience === "Yes") addScore(scores, "scout", 3, reasons, "Has followed a checklist or SOP before.");
  if (form.commissionExperience === "Yes") addAll(scores, 1);
  if (form.canSurviveNoFixedIncome === "Yes") addAll(scores, 1);
  if (form.videoLink.trim()) addScore(scores, "promoter", 1, reasons, "Provided an optional review link.");

  keywordScore(scores, text, ["message", "follow up", "outreach", "reply", "dm", "email", "book", "call"], "closer", 2);
  keywordScore(scores, text, ["research", "verify", "source", "data", "spreadsheet", "list", "duplicate", "lead"], "scout", 2);
  keywordScore(scores, text, ["clip", "video", "edit", "content", "post", "views", "hook", "tiktok", "reels"], "clipper", 2);
  keywordScore(scores, text, ["community", "telegram", "reddit", "discord", "groups", "reply", "placement"], "seeder", 2);
  keywordScore(scores, text, ["influencer", "creator", "partnership", "founder", "audience", "instagram"], "promoter", 2);

  if (form.rejectionResponse.trim().length >= 40) addScore(scores, "scout", 1, reasons, "Explained how they handle rejected proof.");
  if (form.outworkedMoment.trim().length >= 80) addScore(scores, "scout", 1, reasons, "Gave a detailed task-completion example.");
  if (form.sampleResponse.toLowerCase().includes("proof")) addAll(scores, 1);
  if (form.sampleResponse.toLowerCase().includes("spam")) addScore(scores, "seeder", 2, reasons, "Understands spam risk.");

  const sorted = APPLICATION_ROUTES
    .map((route) => ({ route, score: scores[route] }))
    .sort((a, b) => b.score - a.score || APPLICATION_ROUTES.indexOf(a.route) - APPLICATION_ROUTES.indexOf(b.route));
  const primaryRoute = sorted[0]?.route ?? "scout";
  const backupRoute = sorted.find((item) => item.route !== primaryRoute)?.route ?? "promoter";
  if (!reasons.length) reasons.push("Defaulted from general platform fit until a reviewer scores the application.");
  return { primaryRoute, backupRoute, scores, reasons: reasons.slice(0, 5) };
}

export function applicationReviewPacket(form: ApplicationDraft) {
  const recommendation = recommendApplicationRoutes(form);
  return {
    version: APPLICATION_VERSION,
    recommendation,
    contact: {
      cityCountry: form.cityCountry,
      instagramHandle: form.instagramHandle,
      whatsapp: form.whatsapp,
      videoLink: form.videoLink,
    },
    qualification: {
      currentStatus: form.currentStatus,
      workPreference: form.workPreference,
      dealExperience: form.dealExperience,
      commissionExperience: form.commissionExperience,
      obsession: form.obsession,
      outworkedMoment: form.outworkedMoment,
      rejectionResponse: form.rejectionResponse,
      quitReason: form.quitReason,
      loyaltyUnderPressure: form.loyaltyUnderPressure,
      canSurviveNoFixedIncome: form.canSurviveNoFixedIncome,
      firstThirtyDaysPlan: form.firstThirtyDaysPlan,
    },
    sample: {
      version: PLATFORM_SAMPLE.version,
      response: form.sampleResponse,
    },
  };
}

export function prepareApplicationForSubmit(form: ApplicationDraft): ApplicationDraft {
  const recommendation = recommendApplicationRoutes(form);
  return {
    ...form,
    primaryRoute: recommendation.primaryRoute,
    backupRoute: recommendation.backupRoute,
    sampleVersion: PLATFORM_SAMPLE.version,
  };
}

export function validateApplicationStage(stage: number, form: ApplicationDraft): string | null {
  if (stage === 0) {
    if (form.name.trim().length < 2 || form.country.trim().length < 2 || form.timezone.trim().length < 2 || !form.dateOfBirth) return "Fill in every required identity field.";
    const age = ageOnDate(form.dateOfBirth);
    if (age < 16) return "You must be at least 16 to apply.";
    if (age > 100) return "Check your date of birth.";
    if (form.cityCountry.trim().length < 3) return "Add the city and country you operate from.";
    if (form.name.length > 120 || form.country.length > 100 || form.timezone.length > 100 || form.cityCountry.length > 160) return "Shorten the highlighted identity fields.";
    if (form.instagramHandle.length > 120 || form.whatsapp.length > 80) return "Shorten the social contact fields.";
    if (requiresGuardian(form.dateOfBirth) && (form.guardianName.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.guardianEmail) || form.guardianRelationship.trim().length < 2)) {
      return "Add complete guardian details.";
    }
    if (form.guardianName.length > 120 || form.guardianEmail.length > 254 || form.guardianRelationship.length > 100) return "Shorten the guardian details.";
  }
  if (stage === 1) {
    if (!STATUS_OPTIONS.includes(form.currentStatus as typeof STATUS_OPTIONS[number])) return "Choose your current availability situation.";
    if (!WORK_PREFERENCES.includes(form.workPreference as typeof WORK_PREFERENCES[number])) return "Choose the work style that sounds closest.";
    if (!AVAILABILITY_BANDS.includes(form.availabilityBand as typeof AVAILABILITY_BANDS[number])) return "Choose a weekly availability band.";
    if (!YES_NO_OPTIONS.includes(form.dealExperience as typeof YES_NO_OPTIONS[number])) return "Answer the SOP/checklist question.";
    if (!YES_NO_OPTIONS.includes(form.commissionExperience as typeof YES_NO_OPTIONS[number])) return "Answer the approved-proof payout question.";
    if (!YES_NO_OPTIONS.includes(form.canSurviveNoFixedIncome as typeof YES_NO_OPTIONS[number])) return "Answer the extra task money question.";
    if (form.languages.trim().length < 2 || form.languages.length > 200) return "List the languages you can work in.";
    if (form.obsession.trim().length < 10 || form.obsession.length > 500) return "Tell us what simple online tasks you are best at.";
    if (form.whyEntry.trim().length < 40 || form.experience.trim().length < 20) return "Give us a little more detail about your motivation and experience.";
    if (form.whyEntry.length > 4000 || form.experience.length > 4000 || form.tools.length > 1000) return "Shorten the long-form fields.";
    if (form.portfolioUrl) {
      try {
        if (!/^https?:$/.test(new URL(form.portfolioUrl).protocol) || form.portfolioUrl.length > 500) return "Enter a valid portfolio URL.";
      } catch {
        return "Enter a valid portfolio URL.";
      }
    }
    if (form.videoLink) {
      try {
        if (!/^https?:$/.test(new URL(form.videoLink).protocol) || form.videoLink.length > 500) return "Enter a valid video link.";
      } catch {
        return "Enter a valid video link.";
      }
    }
  }
  if (stage === 2) {
    if (form.outworkedMoment.trim().length < 40) return "Tell us about a simple task you completed carefully.";
    if (form.rejectionResponse.trim().length < 30) return "Explain what you would do if proof is rejected.";
    if (form.quitReason.trim().length < 15) return "Answer what would make it hard to keep doing tasks.";
    if (form.loyaltyUnderPressure.trim().length < 30) return "Explain how you will follow rules and avoid spam.";
    if (form.firstThirtyDaysPlan.trim().length < 30) return "Explain how many small tasks you can realistically do first.";
    if (form.sampleResponse.trim().length < 140) return "Your task check answer must be at least 140 characters.";
    if ([form.outworkedMoment, form.rejectionResponse, form.quitReason, form.loyaltyUnderPressure, form.firstThirtyDaysPlan, form.sampleResponse].some((value) => value.length > 6000)) return "Shorten the long-form task answers.";
  }
  if (stage === 3) {
    if (!form.confirmsAgeAndTraining || !form.confirmsCodeOfConduct || !form.confirmsPrivacy || !form.confirmsAccuracy) {
      return "Accept every required consent before sending.";
    }
  }
  return null;
}

export function validateCompleteApplication(form: ApplicationDraft): string | null {
  for (let stage = 0; stage <= 3; stage += 1) {
    const error = validateApplicationStage(stage, form);
    if (error) return error;
  }
  return null;
}

function addAvailability(scores: RouteRecommendation["scores"], band: string, status: string) {
  if (band === "13+" || status === "Fully available" || status === "Between work, ready to start") addAll(scores, 2);
  if (band === "2-4") {
    scores.clipper += 1;
    scores.scout += 1;
  }
}

function addPreference(scores: RouteRecommendation["scores"], preference: string) {
  if (preference.includes("company info")) scores.scout += 8;
  if (preference.includes("approved messages")) scores.closer += 8;
  if (preference.includes("helpful links")) scores.seeder += 8;
  if (preference.includes("short posts")) scores.clipper += 8;
  if (preference.includes("creator or founder")) scores.promoter += 8;
  if (preference.includes("platform pick")) addAll(scores, 2);
}

function keywordScore(scores: RouteRecommendation["scores"], text: string, keywords: string[], route: Exclude<MiddlemanRoute, "unplaced">, amount: number) {
  for (const keyword of keywords) {
    if (text.includes(keyword)) scores[route] += amount;
  }
}

function addScore(scores: RouteRecommendation["scores"], route: Exclude<MiddlemanRoute, "unplaced">, amount: number, reasons: string[], reason: string) {
  scores[route] += amount;
  if (!reasons.includes(reason)) reasons.push(reason);
}

function addAll(scores: RouteRecommendation["scores"], amount: number) {
  for (const route of APPLICATION_ROUTES) scores[route] += amount;
}
